import { LazyMotion, m } from 'framer-motion'
import noop from 'lodash/noop'
import { Trans } from 'react-i18next'
import throttle from 'lodash/throttle'
import moment from 'moment'
import { trim, all, concat, filter, find, propEq } from 'ramda'
import { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router'
import styled from 'styled-components'
import { Skeleton, TextLink, Text, Inline, Box as DSBox } from '@machobear/design-system'
import { checkRestrictedListing } from '@/utils/check-restricted-listing'
import { Avatar } from '@/components/avatar'
import { Button } from '@/components/button'
import { Label } from '@/components/text'
import Icon from '@/components/icon'
import { ChatPerson, ChatPhone, PhoneDisabled, SupportEmail } from '@/components/icons'
import { toast } from '@/components/toast-notifications'
import VerifiedBadge from '@/components/verified-badge'
import SendIcon from '@/features/Chat/images/send-icon.svg?react'
import { useHighlight } from '@/models/toaster/hooks'
import useCurrentSession from '@/hooks/use-current-session'
import useEventListener from '@/hooks/use-event-listener'
import useKeyPress from '@/hooks/use-key-press'
import useLocalStorage from '@/hooks/use-localstorage'
import { select } from '@/store'
import useMediaQuery from '@/hooks/use-media-query'
import { showTenantProfile } from '@/features/Profile/TenantProfileModal'
import { showLandlordProfile } from '@/features/Profile/LandlordProfileModal'
import UnverifiedBadge from '@/components/unverified-badge'
import { showTenantVerificationModal } from '@/features/Chat/tenant-verification-modal'
import { showLandlordVerificationModal } from '@/features/Chat/landlord-verification-modal'
import ChatBanner from '@/components/chat-banner'
import { getImageUrl } from '@/utils/image'
import useRole from '@/hooks/use-role'
import { colors, fontSizes, fontWeights, breakpoints, structure } from '@/styles'
import isVerified from '@/utils/is-verified'
import { Box, Stack } from '@/components/structure'
import { Tour } from '@/components/tour'
import useDelayedMount from '@/hooks/use-delayed-mount'
import Tooltip from '@/components/custom-tooltip'
import { showCopyApplicationModal } from '@/components/copy-application-modal'
import useIsMobile from '@/hooks/use-is-mobile'
import { isIOS } from '@/features/Chat/utils'
import { BulkContext } from '@/pages/Chat/context'
import useIsKeyboardOpen from '@/hooks/use-is-keyboard-open'
import { isNullOrEmpty } from '@/utils'
import { show as ShowEditTemplateModal } from './quick-reply/edit-template-modal'
import { PresetBubble } from './quick-reply'
import FilePreview from './file-preview'
import Message from './chat-message'
import TopStamp from './top-stamp'
import MenuDropdown from './menu-dropdown'
import { showDeleteModal } from './delete-modal'
import LockedBanner from './locked-banner'
import MobileMenuDropdown from './mobile-menu-dropdown'
import { EventsSkeleton } from './event-skeleton'
import * as styles from './index.css'

const loadFeatures = () => import('@/components/animations/features').then(res => res.default)
// Disabling slide animation for iOS for now
// const platform = isIOS() ? 'iOS' : ''
const platform = 'platform'

const DragDropWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  position: relative;
  width: 100%;
  overflow-x: hidden;
  will-change: left;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    &.iOS {
      ${props =>
        props.transistionStage
          ? 'animation: slideOutChatRoom 0.7s forwards'
          : 'animation: slideInChatRoom 0.7s forwards'};

      ${props => props.navigationState === false && `animation: none;`}
    }
  }

  @keyframes slideInChatRoom {
    0% {
      left: 100%;
    }
    100% {
      left: 0%;
    }
  }

  @keyframes slideOutChatRoom {
    0% {
      left: 0%;
    }
    100% {
      left: 100%;
    }
  }
`

const Spacer = styled.div`
  // use spacer for bottom padding due to Firefox bug:
  // https://stackoverflow.com/questions/29986977
  position: relative;
  display: block;
  min-height: 60px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: ${props => (props.keyboardHasBeenClosed ? 40 : 160)}px;
  }
`

const Sticky = styled.div`
  position: absolute;
  top: 0px;
  width: calc(100%);
  margin-left: 0px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    top: 20px;
  }
`

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background-color: ${colors.lightGrey};
`

const AvatarWrapper = styled.div`
  margin-right: 15px;

  ${props =>
    props.clickable &&
    `
  cursor: pointer;
  `}

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin-right: 10px;
  }
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
`

const UserNameText = styled.p`
  font-size: 18px;
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  line-height: 24px;
  letter-spacing: -0.2px;
  margin: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  @media screen and (max-width: ${breakpoints.desktop}) {
    max-width: 250px;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    font-size: 16px;
    line-height: 22px;
    max-width: 140px;
  }
`

const DetailsWrapper = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  height: 100%;
`
// Commenting out top value for now.
// It is removed for T15849
const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.grey};
  padding: 0px 16px;
  gap: 0 12px;
  height: 100%;
  max-height: 73px;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    // No left padding in this breakpoint because the BackIcon has a widened padding
    padding: 0 15px 0 0;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    position: fixed;
    // top: ${structure.header.heightMobile}px;
    z-index: 11;
  }
`

const BackIcon = styled(Icon)`
  display: none;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    // BackIcon's padding-top (notice it's rotated 90deg) must equal HeaderContainer's padding-right in this breakpoint
    padding: 15px 7.5px 7.5px;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: block;
    transform: rotate(-90deg);
    flex-shrink: 0;
    cursor: pointer;
  }
`

const UserInfoContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: auto;
  overflow-y: hidden;
  height: 100%;
`

const MessagesContainer = styled.div`
  position: relative;
  background-color: ${colors.background};
  padding: 15px 16px 0;
  overflow-x: hidden;
  overflow-y: scroll;
  overscroll-behavior: none;
  flex: 1;
  font-size: 12px;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${breakpoints.desktop}) {
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0px 0 10px;
    padding-top: ${props =>
      props.isKeyboardOpen ? 370 : props.keyboardHasBeenClosed ? 210 : 100}px;

    :after {
      padding-bottom: 155px;
    }
  }

  ${props =>
    props.locked &&
    `
    :after {
      padding-bottom: 100px;
    }

    @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
      margin-bottom: 56px;
    }
  `}
`

const MessageInputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  flex: 0 0 152px;
  background-color: ${colors.white};
  padding: 16px 16px 24px;
  padding-right: 0px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex: 0 0 80px;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 15px;
  }
`

const TextAreaWrapper = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  flex: 1;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: auto;
  }
`

const TextInput = styled.textarea`
  font-size: 14px;
  line-height: 19px;
  width: 100%;
  border: 1px solid ${colors.mediumLightGrey};
  resize: none;
  outline: none;
  padding: 15px 30px 15px 15px;
  border-radius: 6px;

  // Note this height is also set dynamically in a useEffect below
  height: 100px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: ${fontSizes.regular};
    line-height: 21px;
    height: 100%;
    padding: 12px 30px 12px 12px;
    max-height: 80px;
    min-height: 44px;
  }
`

const PresetBubbleContainer = styled.div`
  margin-left: 0px;
  padding: 16px 18px 15px 0px;
  background-color: transparent;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 3;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-left: 0px;
    padding-bottom: 25px;
  }
`

const EditPresetBubble = styled(PresetBubble)`
  && {
    min-width: unset;
    padding: 0;
    margin: 0;
    width: 31px;
    height: 31px;
    border-radius: 50%;
    margin-right: 15px;
  }
`

const EditPresetIcon = styled(Icon)`
  padding-top: 2px;
`

const UploadButton = styled.button`
  position: absolute;
  right: 16px;
  top: 12px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 14px;
  height: 20px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 10px;
  }
`

const SendButton = styled.button`
  color: ${colors.white};
  cursor: pointer;
  position: relative;
  box-shadow: 0 2px 4px 0 rgb(0 0 0 / 15%);
  justify-content: center;
  align-items: center;
  display: flex;
  min-width: unset;
  padding: 0 25px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 22px;
  font-weight: 700;
  transition: all 0.2s ease-in-out;

  &:active {
    opacity: 0.3;
  }

  &:disabled {
    background-color: ${colors.grey88};
    color: ${colors.grey56};
    box-shadow: none;
    cursor: default;
  }

  &:hover {
    opacity: 0.9;
  }

  width: 100px;
  height: 42px;
  margin: 0 12px;
  flex-grow: 0;
  background-color: ${colors.blue16};
  border: none;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0 0 0 12px;
    width: 42px;
    border: none;
    box-shadow: none;
    padding: 0;

    svg {
      margin: 0;
      width: 24px;
      height: 24px;
    }
  }
`

const StyledSendIcon = styled(SendIcon)`
  flex-shrink: 0;
  margin-right: 12px;
  path {
    transition: fill 0.2s ease-in-out;
    fill: ${props => (props.disabled ? colors.grey56 : colors.white)};
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0;
    width: 24px;
    height: 24px;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  width: ${props => (props.small ? '80px' : '120px')};
  justify-content: space-between;
`

const ButtonWrapper = styled.div`
  cursor: pointer;
  display: flex;
  transition: opacity 0.2s ease-in-out;
  :hover {
    opacity: 0.5;
  }
`

const UnverifiedWrapper = styled.div`
  margin-left: 5px;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  :hover {
    opacity: 0.5;
  }
`

const PhoneLink = styled.a``

// 60px is the value of quick reply bubble plus padding
const IOSPadding = styled.div`
  padding-top: 60px;
`

const BaseChatRoom = ({
  avatarUrl,
  events,
  handleSendFile,
  handleSendMessage,
  handleInviteToApply,
  sendingInvite,
  loading = false,
  loadingBottom = false,
  listing = {},
  timeline,
  name,
  address,
  onScrollTop = noop,
  onScrollBottom = noop,
  handleBack,
  otherUser,
  presetMessages = [],
  isPresetEditEnabled = true,
  isSupportRoom,
  history,
  showMessageDetailsDrawer = () => {},
  us = [],
  them = [],
  getAddressUrl = () => {},
  handleShowMembers = () => {},
  isLocked = false,
  showInviteToApply = false,
  showViewApplication = false,
  // Commenting out has Applied to use the value from
  // Chat timeline instead of current_timeline
  // Clean up with proper selector later
  // hasApplied = false,
  listingId,
  is_favourite = false,
  match: {
    params: { timelineId }
  }
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isKeyboardOpen = useIsKeyboardOpen() && isIOS()
  const [isQuickChatsDimmed, setIsQuickChatsDimmed] = useState(false)
  const [keyboardHasBeenOpened, setKeyboardHasBeenOpened] = useState(false)
  const [keyboardHasBeenClosed, setKeyboardHasBeenClosed] = useLocalStorage(
    'keyboardHasBeenClosed',
    {}
  )

  // Temp fix for hasApplied
  const selectedTimeline = useSelector(select.chat.timelines).find(
    timeline => timeline.timeline_room === timelineId
  )
  const hasApplied = !!selectedTimeline?.applications?.id

  useEffect(() => {
    if (isKeyboardOpen === true) {
      setKeyboardHasBeenOpened(true)
    }
    if (isKeyboardOpen === false && keyboardHasBeenOpened) {
      setKeyboardHasBeenClosed({ ...keyboardHasBeenClosed, [timeline?.timeline_room]: true })
    }
  }, [isKeyboardOpen])
  const me = useCurrentSession()?.id
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const applicationId = timeline?.applications?.id
  const hasApplication = !isNullOrEmpty(applicationId)

  useEffect(() => {
    if (hasApplication) {
      dispatch.application.loadNoLoading(applicationId)
    }
  }, [applicationId, hasApplication, dispatch.application])

  const application = useSelector(state =>
    select?.application.applicationDetail(state, {
      applicationId
    })
  )

  const isAlternateContainerStyling =
    (isIOS() && keyboardHasBeenClosed?.[timeline?.timeline_room] === true) ||
    isKeyboardOpen === true
  const [files, setFiles] = useState([])
  const [, setHasScrolled] = useState(false)
  const [textAreaOffset, setTextAreaOffset] = useState(0)
  const [hideTour, setHideTour] = useState(false)
  const messagesContainerRef = useRef()
  const inputRef = useRef()
  const autoScroll = useRef(true)
  const [message, setMessage] = useState('')
  const isEmpty = !trim(message)
  const isUserAgent = useIsMobile()
  const [addOpen, setAddOpen] = useState()
  const allUsersWithAvatars = [...them, ...us].reduce((acc = {}, user) => {
    return {
      ...acc,
      [user.id]: getImageUrl(user.avatar?.aws_s3_key, {
        width: 80,
        height: 80
      })
    }
  }, {})

  const allUsersWithColours = [...them, ...us].reduce((acc = {}, user) => {
    return {
      ...acc,
      [user.id]: user.colour
    }
  }, {})
  const isGroupChat = [...them, ...us].length > 2
  const listingVerified = listing?.verified_state_machine === 'approved'
  const listingPending = listing?.verified_state_machine === 'pending'
  const verifiedUserIds = filter(user => isVerified(user, user?.company), them)

  const isLandlord = useRole() === 'landlord'
  const isListingImported = checkRestrictedListing(listing.tags)

  const showListingPlatformLink = isLandlord && isListingImported

  const allIdsVerified = verifiedUserIds?.length === them?.length
  const renterAndMultiLandlord = !isLandlord && them.length > 1
  const [transitionStage, setTransistionStage] = useState(false)
  const bulkContext = useContext(BulkContext)
  const { navigationState, setNavigationState } = bulkContext

  const sourceAbbreviation = listing?.external_info?.source_info_abbreviation ?? null

  useEffect(() => {
    const root = document.documentElement
    const value = isMobile ? '124px' : '-12px'
    root.style.setProperty('--toaster-bottom', value)
    return () => {
      const current = root.style.getPropertyValue('--toaster-bottom')
      if (!current || current !== value) return
      root.style.removeProperty('--toaster-bottom')
    }
  }, [isMobile])

  const inviteToApply = useCallback(
    (...args) => {
      setHideTour(true)
      if (typeof handleInviteToApply === 'function') {
        handleInviteToApply(...args)
      }
    },
    [handleInviteToApply]
  )

  const showName = timeline && timeline.users.us.length + timeline.users.them.length.length > 2

  useEffect(() => {
    autoScroll.current = true
  }, [timelineId])

  const showApplyToListing = timeline?.state_machine === 'active' && !hasApplied && !isLandlord

  const showQuickReply = !isLocked && timeline?.state_machine !== 'history'

  const onScroll = useMemo(
    () =>
      throttle(event => {
        const el = event.target
        const scrollTop = el.scrollTop
        const scrollHeight = el.scrollHeight - el.clientHeight
        const ratio = scrollTop / scrollHeight
        if (ratio > 0.98) {
          setIsQuickChatsDimmed(false)
        }
        if (ratio < 0.00025) {
          onScrollTop(event)
        }
        if (ratio > 0.995) {
          onScrollBottom(event)
        }
        if (ratio < 0.98) {
          setIsQuickChatsDimmed(true)
          autoScroll.current = false
        } else {
          autoScroll.current = true
        }
        setHasScrolled(value => value || ratio < 0.985)
      }, 500),
    [onScrollTop, onScrollBottom]
  )

  useEffect(() => {
    setHasScrolled(false)
  }, [listingId])

  useEventListener('scroll', onScroll, messagesContainerRef.current)

  useEffect(() => {
    const container = messagesContainerRef.current
    const difference = container?.scrollHeight - (container?.offsetHeight + container?.scrollTop)

    const scrollHeight = container?.scrollHeight + difference
    if (autoScroll.current) {
      container.scrollTop = scrollHeight
    }
  }, [events, messagesContainerRef?.current?.scrollHeight])

  const acceptedFileTypes =
    'image/png, image/jpg, image/jpeg, application/pdf, application/msword, text/plain'
  const { getInputProps, open } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg', '.jpeg'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'text/plain': ['.txt']
    },
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: handleSendFile
  })

  const handleSendClick = e => {
    if (isEmpty) return
    autoScroll.current = true
    handleSendMessage(message)
    setMessage('')
  }

  const handleReturnKey = e => {
    if (e.shiftKey || e.ctrlKey || e.altKey) return true
    e.preventDefault()
    handleSendClick()
  }

  useKeyPress('Enter', handleReturnKey, inputRef.current)

  // Auto adjust height of textarea on mobile screen sizes
  useEffect(() => {
    if (!inputRef.current) return
    if (!isMobile) {
      inputRef.current.style.height = '100px'
      return
    }
    inputRef.current.style.height = '0px'
    const scrollHeight = inputRef.current.scrollHeight
    inputRef.current.style.height = scrollHeight + 'px'
    setTextAreaOffset(Math.min(scrollHeight + 19, 98))
  }, [message, isMobile])

  const handleDragFile = items => {
    const mergedFiles = concat(files, items)
    const isValid =
      all(item => acceptedFileTypes.includes(item.type), mergedFiles) && mergedFiles.length <= 10
    if (!isValid)
      return toast(
        t('b.chat.view.send_files.error', 'Only valid file types and max. 10 files per upload.'),
        {
          title: t('common.error', 'Error'),
          type: 'error'
        }
      )
    setFiles(files => concat(files, items))
  }

  const { getRootProps, isDragActive } = useDropzone({
    multiple: true,
    noKeyboard: true,
    noClick: true,
    onDrop: handleDragFile
  })

  const showFileUploadPanel = isDragActive || files.length > 0

  const handleApplyToListing = () => {
    history.push(`/application/create/${listingId}`)
  }

  const handleViewApplication = () => {
    history.push(`/chat/${timeline?.timeline_room}/application/${timeline?.applications?.id}`)
  }

  const checkChatGrouping = (msg, event, isNext) => {
    if (msg && msg.user_id === event.user_id && msg.event_type === 'chat') {
      const first = isNext ? msg.created_at : event.created_at
      const next = isNext ? event.created_at : msg.created_at
      const minutesSinceLastMessage = moment(first).diff(next, 'minutes')
      return minutesSinceLastMessage < 60 ? msg : null
    }
    return null
  }

  const handleCopyApplicant = () => {
    showCopyApplicationModal({ application, history, listingId })
  }

  const handleDelete = () =>
    showDeleteModal({ name: nameText, timelineRoomId: timeline?.timeline_room, history })

  const addressUrl = getAddressUrl()
  const nameText = `${name}${them.length > 1 ? ` +${them.length - 1}` : ''}`
  const handleViewProfile = () => {
    if (isSupportRoom) return

    if (isLandlord) {
      showTenantProfile({
        data: otherUser,
        timelineId: timeline?.timeline_room,
        showInviteToApply,
        showViewApplication,
        inviteToApply,
        applicationId: timeline.applications?.id,
        history
      })
    } else if (!isLandlord) {
      if (them.length > 1) {
        handleShowMembers()
      } else {
        showLandlordProfile({
          data: otherUser
        })
      }
    }
  }
  const handleViewVerification = () => {
    if (isLandlord) {
      showTenantVerificationModal({
        verificationStatus: otherUser.identity_verified_state || 'unverified',
        timelineRoomId: timeline?.timeline_room
      })
    } else {
      showLandlordVerificationModal({
        landlords: them,
        listingVerified,
        listingPending,
        timelineRoomId: timeline?.timeline_room
      })
    }
  }

  const firstMessageTime = events[0]?.created_at

  const renderDateForFirstMessageOfDay = (event, index) => {
    const previousIndex = Math.max(0, index - 1)
    const previousMessage = events[previousIndex].created_at
    const currentMessage = event.created_at
    const isFirstMessageOfDay =
      index === 0 || moment(currentMessage).diff(previousMessage, 'days') >= 1

    const hasSent = event?.pending !== true
    return isFirstMessageOfDay && hasSent ? (
      <TopStamp date={index === 0 ? firstMessageTime : event.created_at} />
    ) : null
  }

  const renderLandlordBadges = () => {
    if (allIdsVerified && listingVerified)
      return (
        <ButtonWrapper>
          <VerifiedBadge
            pointer
            onClick={handleViewVerification}
            type="id"
            width={21}
            height={21}
            bottom={2}
            verified={allIdsVerified}
          />
        </ButtonWrapper>
      )

    if (!allIdsVerified && !listingVerified)
      return (
        <UnverifiedWrapper onClick={handleViewVerification}>
          <UnverifiedBadge showSmallOnMobile top={4} bottom={4} />
        </UnverifiedWrapper>
      )

    if (allIdsVerified || listingVerified)
      return (
        <>
          <ButtonWrapper>
            <VerifiedBadge
              pointer
              onClick={handleViewVerification}
              type="id"
              width={21}
              height={21}
              bottom={2}
              verified={allIdsVerified}
            />
          </ButtonWrapper>
          <UnverifiedWrapper onClick={handleViewVerification}>
            <UnverifiedBadge showSmallOnMobile top={4} bottom={4} />
          </UnverifiedWrapper>
        </>
      )
  }

  useEffect(() => {
    window.addEventListener('popstate', function (event) {
      setNavigationState(false)
    })
  }, [window])

  return (
    <DragDropWrapper
      {...getRootProps()}
      transistionStage={transitionStage}
      navigationState={navigationState}
      className={platform}
    >
      <Wrapper>
        <HeaderContainer>
          <UserInfoContainer>
            <BackIcon
              id="arrow"
              height={36.5}
              width={33}
              onClick={() => {
                handleBack()
                setTransistionStage(true)
                setNavigationState(true)
              }}
            />
            <DetailsWrapper>
              {!isSupportRoom && (
                <AvatarWrapper onClick={handleViewProfile} clickable={!isSupportRoom}>
                  <Avatar location={avatarUrl} width={40} height={40} />
                </AvatarWrapper>
              )}

              <InfoWrapper>
                <NameWrapper>
                  {nameText ? (
                    <>
                      <UserNameText>{nameText}</UserNameText>
                      {!isSupportRoom &&
                        (!isLandlord ? (
                          renderLandlordBadges()
                        ) : allIdsVerified ? (
                          <ButtonWrapper>
                            <VerifiedBadge
                              pointer
                              onClick={handleViewVerification}
                              type="id"
                              width={21}
                              height={21}
                              bottom={2}
                              verified={allIdsVerified}
                            />
                          </ButtonWrapper>
                        ) : (
                          <UnverifiedWrapper onClick={handleViewVerification}>
                            <UnverifiedBadge showSmallOnMobile top={4} bottom={4} />
                          </UnverifiedWrapper>
                        ))}
                    </>
                  ) : (
                    <Skeleton.Header width={90} />
                  )}
                </NameWrapper>
                <DSBox marginTop="xxxsmall">
                  <Inline space="xxsmall" alignY="center">
                    {address ? (
                      <>
                        <DSBox className={styles.addressLink}>
                          <Text size="xsmall" tone="link" maxLines={1}>
                            <TextLink target="_blank" href={addressUrl} weight="weak">
                              {address}
                            </TextLink>
                          </Text>
                        </DSBox>
                        {showListingPlatformLink && (
                          <Text size="xsmall" tone="neutral">
                            |
                          </Text>
                        )}
                      </>
                    ) : (
                      !isSupportRoom && <Skeleton.Shape width={60} height={14} />
                    )}

                    {showListingPlatformLink && listing?.external_info?.external_url && (
                      <Text size="xsmall" tone="link">
                        <TextLink
                          href={listing?.external_info?.external_url}
                          weight="weak"
                          target="_blank"
                        >
                          <Trans
                            i18nKey="b.chat.view.listing_platform.text"
                            value={{ sourceAbbreviation }}
                          >
                            View listings on {{ sourceAbbreviation }}
                          </Trans>
                        </TextLink>
                      </Text>
                    )}
                  </Inline>
                </DSBox>
              </InfoWrapper>
            </DetailsWrapper>
          </UserInfoContainer>
          {!isSupportRoom ? (
            <ButtonContainer small={renterAndMultiLandlord || !isLandlord}>
              <ButtonWrapper onClick={handleShowMembers}>
                <Tooltip
                  text={t('b.chat.view.chat_members.text', 'Chat Members')}
                  placement="bottom"
                >
                  <ChatPerson />
                </Tooltip>
              </ButtonWrapper>

              {isLandlord && (
                <ButtonWrapper>
                  {hasApplied ? (
                    <PhoneLink href={`tel:${otherUser?.phone_number}`}>
                      <Tooltip
                        text={`${t('b.chat.view.call.text', 'Call')} ${name}`}
                        placement="bottom"
                      >
                        <ChatPhone />
                      </Tooltip>
                    </PhoneLink>
                  ) : (
                    <Tooltip
                      text={`${t(
                        'b.chat.view.disabled_call.text',
                        'Renter needs to apply or be invited to a contract to enable calls.'
                      )}`}
                      placement="bottom"
                    >
                      <PhoneDisabled />
                    </Tooltip>
                  )}
                </ButtonWrapper>
              )}
              {isMobile ? (
                <MobileMenuDropdown name={nameText} data={timeline} is_favourite={is_favourite} />
              ) : (
                <MenuDropdown name={nameText} data={timeline} is_favourite={is_favourite} />
              )}
            </ButtonContainer>
          ) : (
            <ButtonWrapper>
              <a target="_blank" rel="noopener noreferrer" href="mailto:support@liv.rent">
                <SupportEmail />
              </a>
            </ButtonWrapper>
          )}
        </HeaderContainer>

        <MessagesContainer
          isKeyboardOpen={isKeyboardOpen}
          keyboardHasBeenClosed={isAlternateContainerStyling}
          ref={messagesContainerRef}
          locked={isLocked}
        >
          {isLocked && (
            <LockedBanner
              handleDelete={handleDelete}
              hasApplication={hasApplication}
              handleCopyApplicant={handleCopyApplicant}
              isLandlord={isLandlord}
            />
          )}
          <LazyMotion features={loadFeatures}>
            {showFileUploadPanel && (
              <m.div
                initial={{ position: 'absolute', top: 2000 }}
                animate={{ top: -55 }}
                transition={{ type: 'spring', damping: 20, mass: 1, stiffness: 130 }}
              >
                <FilePreview handleSendFile={handleSendFile} files={files} setFiles={setFiles} />
              </m.div>
            )}
          </LazyMotion>
          {loading && <EventsSkeleton data-testid="chat-loading" />}
          {!loading && <ChatBanner />}
          {events?.map((event, index) => {
            const currentUserData = isSupportRoom
              ? null
              : find(propEq('id', event?.user_id))([...timeline?.users.them, ...timeline?.users.us])
            const isMe = me === event.user_id
            const previousMessage = events[index - 1]
            const nextMessage = events[index + 1]
            const isLastMessageOfUser = events[index].user_id !== events[index + 1]?.user_id
            const isFirstMessageOfUser =
              index === 0 || events[index].user_id !== events[index - 1]?.user_id

            return (
              <div key={`event-${event.id}`}>
                {renderDateForFirstMessageOfDay(event, index)}
                <Message
                  avatar={
                    isLastMessageOfUser && isGroupChat && !isMe
                      ? allUsersWithAvatars[event.user_id]
                      : null
                  }
                  colour={allUsersWithColours?.[event.user_id]}
                  userData={currentUserData}
                  isLocked={isLocked}
                  isLastMessageOfUser={isLastMessageOfUser}
                  showUserName={isFirstMessageOfUser && isGroupChat}
                  isGroupChat={isGroupChat}
                  history={history}
                  showDrawer={() => showMessageDetailsDrawer(event)}
                  isRead={event.is_read}
                  key={event.id}
                  isSupportRoom={isSupportRoom}
                  event={event}
                  position={isMe ? 'right' : 'left'}
                  showName={showName}
                  isFirst={index === 0}
                  previousMessage={checkChatGrouping(previousMessage, event, false)}
                  nextMessage={checkChatGrouping(nextMessage, event, true)}
                />
              </div>
            )
          })}
          {loadingBottom && <EventsSkeleton data-testid="chat-loading-bottom" />}
          {isIOS() && keyboardHasBeenClosed && <IOSPadding />}
          <Spacer keyboardHasBeenClosed={isAlternateContainerStyling} />
        </MessagesContainer>
        {!isLocked && (
          <>
            <MessageInputContainer>
              {showQuickReply && (
                <Sticky isUserAgent={isUserAgent} offset={textAreaOffset}>
                  <Highlight hide={addOpen}>
                    <PresetBubbleContainer id="quick-reply-bar" onClick={() => setAddOpen(true)}>
                      {showInviteToApply && (
                        <HighlightApplication hide={hideTour}>
                          <PresetBubble
                            isDim={isQuickChatsDimmed}
                            disabled={sendingInvite}
                            bold
                            id="invite-to-apply-bubble"
                            onClick={inviteToApply}
                            pinned
                          >
                            {t('b.chat.view.application_invitation.link', 'Invite to Apply')}
                          </PresetBubble>
                        </HighlightApplication>
                      )}

                      {showViewApplication && (
                        <HighlightApplication>
                          <PresetBubble
                            isDim={isQuickChatsDimmed}
                            bold
                            id="view-application-bubble"
                            onClick={handleViewApplication}
                            pinned
                          >
                            {t('l.chat.view.view_application.button', 'View Application')}
                          </PresetBubble>
                        </HighlightApplication>
                      )}

                      {showApplyToListing && (
                        <PresetBubble
                          isDim={isQuickChatsDimmed}
                          bold
                          onClick={handleApplyToListing}
                          pinned
                        >
                          {t('b.chat.view.quick_reply_apply_now.link', 'Apply now')}
                        </PresetBubble>
                      )}
                      {presetMessages &&
                        presetMessages.map(preset => (
                          <PresetBubble
                            isDim={isQuickChatsDimmed}
                            applyMessage={setMessage}
                            key={preset.id}
                            wide={isSupportRoom}
                          >
                            {preset.content}
                          </PresetBubble>
                        ))}
                      {isPresetEditEnabled && (
                        <EditPresetBubble
                          isDim={isQuickChatsDimmed}
                          onClick={ShowEditTemplateModal}
                        >
                          <EditPresetIcon id="edit" width={17} height={17} />
                        </EditPresetBubble>
                      )}
                    </PresetBubbleContainer>
                  </Highlight>
                </Sticky>
              )}
              <TextAreaWrapper>
                <TextInput
                  isDragActive={isDragActive}
                  ref={inputRef}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  maxLength={1500}
                  placeholder={t(
                    'b.chat.view.type_your_message.placeholder',
                    'Type your message...'
                  )}
                />
                <input {...getInputProps()} data-testid="upload-button" />
                <UploadButton onClick={open}>
                  <Icon width={10} height={20} id="attachment" />
                </UploadButton>
              </TextAreaWrapper>
              <SendButton
                disabled={isEmpty}
                onClick={handleSendClick}
                title={t('b.chat.view.send.button', 'Send')}
                type="button"
                tabIndex={isEmpty ? -1 : 0}
              >
                <StyledSendIcon disabled={isEmpty} />
                {!isMobile && t('b.chat.view.send.button', 'Send')}
              </SendButton>
            </MessageInputContainer>
          </>
        )}
      </Wrapper>
    </DragDropWrapper>
  )
}

const Highlight = ({ children, hide: propHide }) => {
  const { hide, next: onBeforeClose } = useHighlight('chat_leads.create')
  const { t } = useTranslation()
  const delay = useDelayedMount(1000)
  const steps = useMemo(
    () => [
      {
        selector: '#quick-reply-bar',
        content: ({ next }) => (
          <Stack gutter={16} maxWidth={216}>
            <Stack gutter={4}>
              <Label fontSize="14px">
                {t('b.toaster.view.chat_leads.create.tour.title', 'Quick reply templates')}
              </Label>
              <Box color={colors.grey32}>
                <Label fontSize="12px" type="small">
                  {t(
                    'b.toaster.view.chat_leads.create.tour.description',
                    'Customize, edit and rearrange quick reply templates to get through your overflowing inbox.'
                  )}
                </Label>
              </Box>
            </Stack>
            <Box width="100%" display="flex" justify="flex-end">
              <Box width={96}>
                <Button size="small" onClick={next}>
                  {t('b.toaster.view.key_features.tour.button', 'Got it!')}
                </Button>
              </Box>
            </Box>
          </Stack>
        )
      }
    ],
    [t]
  )
  return (
    <Tour
      hide={hide || !delay || propHide}
      steps={steps}
      position="top"
      positionStrategy="none"
      showCloseButton={false}
      onBeforeClose={onBeforeClose}
      allowInteraction
    >
      {children}
    </Tour>
  )
}

const HighlightApplication = ({ children, hide: propHide }) => {
  const { hide, next: onBeforeClose } = useHighlight('application.invite', 'invite_apply')
  const { t } = useTranslation()
  const delay = useDelayedMount(1000)
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const steps = useMemo(
    () =>
      delay
        ? [
            {
              selector: '#invite-to-apply-bubble',
              content: ({ next }) => (
                <Stack gutter={16} maxWidth={216}>
                  <Stack gutter={4}>
                    <Label fontSize="14px">
                      {t(
                        'b.toaster.view.application.invite.invite_apply.tour.title',
                        'Invite to apply'
                      )}
                    </Label>
                    <Box color={colors.grey32}>
                      <Label fontSize="12px" type="small">
                        {t(
                          'b.toaster.view.application.invite.invite_apply.tour.description',
                          'Use our quick reply feature to invite a prospective tenant to apply to a listing.'
                        )}
                      </Label>
                    </Box>
                  </Stack>
                  <Box width="100%" display="flex" justify="flex-end">
                    <Box width={96}>
                      <Button size="small" onClick={next}>
                        {t('b.toaster.view.key_features.tour.button', 'Got it!')}
                      </Button>
                    </Box>
                  </Box>
                </Stack>
              )
            }
          ]
        : [],
    [t, delay]
  )
  return (
    <Tour
      hide={propHide || hide || !delay}
      steps={steps}
      point={displayMobile ? 'bottom' : 'right'}
      position={displayMobile ? 'top' : 'left'}
      showCloseButton={false}
      onBeforeClose={onBeforeClose}
      allowInteraction
    >
      {children}
    </Tour>
  )
}

export default withRouter(BaseChatRoom)
